// Canonical Gantt scheduling engine.
// Single source of truth for start/duration calculation — used by FlowchartBuilder and ProjectGanttView.
// All helpers are pure functions (no closures, no component state).

export interface ScheduleBox {
  id: number;
  text?: string;        // startday boxes: display value is the day number
  type?: string;        // 'startday' | 'callout' | 'regular'
  duration?: string;    // e.g. "3d", "2w", "1.5h"
  alwaysOn?: boolean;   // task may run 24/7, not limited to work hours
  is24_5?: boolean;     // task runs 24h/day but weekdays only (Mon–Fri)
  scheduledStart?: string;  // ISO date — pins start, overrides dependency chain
  earliestStart?: string;   // ISO date or "W{n}" week number — lower bound only
  // Subitems (see calcSubitemDuration/applySubitemRollup below): parentBoxId marks a
  // box as nested under another; rolledUpDuration, when set by applySubitemRollup,
  // replaces the box's own `duration` string for scheduling purposes everywhere
  // calcSchedule would otherwise call parseDuration(box.duration, ...) — already in
  // schedule-column units (same as ScheduleOptions.durationUnit), so no string
  // round-trip through parseDuration is needed or attempted.
  parentBoxId?: number;
  rolledUpDuration?: number;
  // User-marked milestone (canvas/Gantt purple diamond badge) — see calcCriticalPath's
  // own comment: when at least one task is marked, it anchors the critical path's
  // project-end instead of the single longest-finishing task in the whole graph.
  importantFinish?: boolean;
}

export interface ScheduleLink {
  id?: number;
  fromBox: number;
  toBox: number;
}

export interface ScheduleOptions {
  durationUnit?: 'hours' | 'days' | 'weeks';
  fullDaySchedule?: boolean;   // treat every day as 24h (vs the configured workday)
  workHoursOnly?: boolean;     // skip non-working days in column list
  workDays?: number[];         // which days of week are working (0=Sun…6=Sat), default [1-5]
  nonWorkingDays?: string[];   // YYYY-MM-DD strings of extra non-working days
  startDate?: Date;            // t=0 reference date
  dayStartHour?: number;       // workday start hour (0-23), default 9
  hoursPerDay?: number;        // workday length in hours (dayEndHour - dayStartHour), default 8
}

export interface ScheduledTask extends Omit<ScheduleBox, 'duration'> {
  start: number;     // offset in gantt units from t=0
  duration: number;  // computed duration in gantt units (replaces the raw string)
  endColForConflict: number; // effective end column for conflict detection (accounts for alwaysOn clock overshoot)
}

export interface VisualPositions {
  positions: Map<number, { start: number; duration: number }>;
  // Edges ("fromId->toId") that must be excluded from calcCriticalPath's backward
  // pass: a real-time tie-break loser's edge into the successor it tied with —
  // see the comment in computeVisualPositions for why this can't be expressed by
  // adjusting the loser's own duration instead.
  suppressedEdges: Set<string>;
  // Real (unsnapped) end clock time per alwaysOn/is24_5 task, hours mode only.
  // calcSchedulingConflicts needs this for pinned-with-time-of-day successors:
  // the grid-snapped column (which rounds an overnight finish forward to "next
  // work morning") can't tell a predecessor finishing at 2am from one finishing
  // at 9am, but a successor pinned to e.g. 00:00 genuinely can start before either.
  realEnds24: Map<number, Date>;
}

// ── Pure helpers (exported so callers can reuse without re-importing) ─────────

/** Convert a duration value between display units (hours/days/weeks), using the
 *  flowchart's configured workday length and a 5-day work week for weeks. Purely a
 *  label-text conversion (e.g. Gantt bar duration labels) — never used for scheduling,
 *  which always stays in the flowchart's own configured durationUnit. */
export function convertDurationUnit(value: number, fromUnit: string, toUnit: string, hoursPerDayVal: number): number {
  if (fromUnit === toUnit) return value;
  const hours = fromUnit === 'hours' ? value : fromUnit === 'weeks' ? value * hoursPerDayVal * 5 : value * hoursPerDayVal;
  if (toUnit === 'hours') return hours;
  if (toUnit === 'weeks') return hours / (hoursPerDayVal * 5);
  return hours / hoursPerDayVal;
}

export function parseDuration(
  d: string | undefined,
  alwaysOn: boolean,
  durationUnit: string,
  fullDaySchedule: boolean,
  is24_5 = false,
  hoursPerDay = 8,
): number {
  if (!d) return 1;
  const m = d.match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
  if (!m) return 1;
  const n = parseFloat(m[1]);
  if (n === 0) return 0;
  const srcUnit = (m[2] || 'd').toLowerCase();
  const is24h = alwaysOn || is24_5 || fullDaySchedule;
  const hours = srcUnit === 'h' ? n : srcUnit === 'd' ? n * hoursPerDay : n * hoursPerDay * 5;
  // In hours mode, t.duration for an is24h (alwaysOn/is24_5) box IS real clock hours —
  // every consumer (is24hEndFromStart/addWeekdayHours, and this file's own
  // getIs24hRealEnd) adds it directly to a real clock Date with no further
  // conversion (see is24hEndFromStart's own doc comment: "task.duration here is
  // calcSchedule's own value, real clock hours"). A "4d" box therefore means 4
  // work-days' worth of effort (36 hours at a 9h workday) — which, run 24/7, takes
  // exactly 36 real hours, the same `hours` value a regular task already gets.
  // Previously this divided by a "compressionRatio" (24/hoursPerDay) that nothing
  // downstream ever multiplied back — shrinking a 4-work-day task to ~1.5 real
  // days' worth of hours the moment it was switched to alwaysOn, and throwing off
  // every displayed clock time for it from then on.
  if (durationUnit === 'hours') return Math.max(0.5, hours);
  if (durationUnit === 'days') return Math.max(0.1, hours / (is24h ? 24 : hoursPerDay));
  // weeks — no rounding so short tasks don't become 1 week
  return Math.max(0.1, hours / (is24h ? 24 * 5 : hoursPerDay * 5));
}

/**
 * Formats a value already expressed in `durationUnit`'s own units (a schedule-column
 * number, e.g. calcSubitemDuration's return) back into the box.duration string format
 * ("3d", "2.5h", "1w"). NOT a general inverse of parseDuration: it deliberately never
 * decompresses is24h/is24_5 hours, since a rolled-up value never needs that — see
 * calcSchedule's getDur, which uses rolledUpDuration verbatim instead of re-parsing a
 * duration string for exactly this reason. Only meant for the one caller that has to
 * turn such a value back into a display string (syncing a parent box's own duration
 * field to its subitems), not for anything that feeds back into scheduling math.
 */
export function formatDuration(value: number, durationUnit: string): string {
  const suffix = durationUnit === 'hours' ? 'h' : durationUnit === 'weeks' ? 'w' : 'd';
  const rounded = Math.round(value * 10) / 10;
  return `${rounded}${suffix}`;
}

/** Adds `hours` clock-hours to `start`, skipping non-work days (24/5 arithmetic). */
export function addWeekdayHours(start: Date, hours: number, nonWorkingDays: string[], workDays: number[] = [1,2,3,4,5]): Date {
  const nwSet = new Set(nonWorkingDays);
  const result = new Date(start);
  let remainingMs = Math.round(hours * 3600000);
  while (remainingMs > 0) {
    const dow = result.getDay();
    const ds = `${result.getFullYear()}-${String(result.getMonth() + 1).padStart(2, '0')}-${String(result.getDate()).padStart(2, '0')}`;
    if (!workDays.includes(dow) || nwSet.has(ds)) {
      result.setDate(result.getDate() + 1); result.setHours(0, 0, 0, 0); continue;
    }
    const midnight = new Date(result); midnight.setDate(midnight.getDate() + 1); midnight.setHours(0, 0, 0, 0);
    const msUntilMidnight = midnight.getTime() - result.getTime();
    if (remainingMs <= msUntilMidnight) {
      result.setTime(result.getTime() + remainingMs); remainingMs = 0;
    } else {
      remainingMs -= msUntilMidnight;
      result.setDate(result.getDate() + 1); result.setHours(0, 0, 0, 0);
    }
  }
  return result;
}

/**
 * Real (unsnapped) clock end of an is24h (alwaysOn/is24_5) box/task, given its real
 * clock start and duration in real hours. Single source of truth for this — it was
 * previously reimplemented separately in calcSchedule, computeVisualPositions,
 * GanttPanel.tsx, and lib/svgExport.ts, and had already silently drifted: svgExport's
 * copy hardcoded Mon-Fri instead of using the flowchart's actual configured workDays
 * for is24_5 tasks, so an export could show a different end time than the live Gantt
 * view for any flowchart with a non-default work week.
 */
export function is24hEndFromStart(
  start: Date,
  durationHours: number,
  is24_5: boolean,
  nonWorkingDays: string[],
  workDays: number[] = [1, 2, 3, 4, 5],
): Date {
  return is24_5
    ? addWeekdayHours(start, durationHours, nonWorkingDays, workDays)
    : new Date(start.getTime() + durationHours * 3600000);
}

/**
 * Shared recursive walker for "real (unsnapped) clock start of an is24h (alwaysOn/is24_5)
 * box/task, walking its whole is24h ancestor chain in real time." Used by calcSchedule
 * (which hasn't computed schedule-column positions yet, so resolves a predecessor's
 * contribution by recursing into its own calcStart), computeVisualPositions (whose
 * tasks already have start/duration resolved, so it just reads them), and
 * useGanttSchedule.ts's getAlwaysOnClockStart (same as computeVisualPositions, but for
 * the canvas/box-label display path rather than Gantt bar positions) — each supplies
 * its own `resolvePredecessor` callback to bridge that difference, so this only shares
 * the genuinely-identical skeleton: scheduledStart-pin parsing, the cache/cycle-guard,
 * and "latest of all predecessors" reduction. Exported so useGanttSchedule.ts (a
 * different file/layer) can use it too — see CLAUDE.md's standing warning against
 * reimplementing is24h logic separately: useGanttSchedule.ts's getAlwaysOnClockStart
 * used to be exactly such a separate, hand-rolled reimplementation of this, and two of
 * its three independent bugs (an own-task start off by a day, an earliestStart losing
 * its exact date on a weekend) were specifically divergences from what this shared
 * skeleton already got right.
 *
 * NOTE: the call sites have small, pre-existing behavioral differences, preserved here
 * as parameters rather than silently unified (unifying them would be a scheduling
 * behavior change, not just a refactor):
 *   - the hour defaulted when a scheduledStart pin has no time-of-day component
 *     (calcSchedule / useGanttSchedule.ts: midnight, matching calcStart's own is24h
 *     pin encoding; computeVisualPositions: the configured workday start hour)
 *   - whether a startday-marker predecessor / an earliestStart floor is considered
 *     (calcSchedule: no; computeVisualPositions / useGanttSchedule.ts: yes) — encoded
 *     in each caller's own resolvePredecessor / applyEarliestStart callbacks.
 * computeVisualPositions previously had no cycle-guard at all here (calcSchedule did) —
 * sharing this skeleton adds one for it too, which only matters for a malformed/cyclic
 * arrow graph and never changes behavior for a normal (acyclic) one.
 */
export function walkIs24hRealStart(
  id: number,
  cache: Record<number, Date>,
  visiting: Set<number>,
  cfg: {
    getScheduledStart: (id: number) => string | undefined;
    scheduledStartDefaultHour: number;
    fallback: Date;
    getPredecessorIds: (id: number) => number[];
    resolvePredecessor: (predId: number) => Date | null;
    applyEarliestStart?: (id: number, result: Date) => Date;
  },
): Date {
  if (cache[id]) return cache[id];
  if (visiting.has(id)) return cfg.fallback; // cycle guard
  visiting.add(id);
  let result: Date;
  const ss = cfg.getScheduledStart(id);
  if (ss) {
    const [datePart, timePart] = ss.split('T');
    const [sy, sm, sd] = datePart.split('-').map(Number);
    result = new Date(sy, sm - 1, sd, cfg.scheduledStartDefaultHour, 0, 0);
    if (timePart) {
      const [h, mi] = timePart.split(':').map(Number);
      result.setHours(h || 0, mi || 0, 0, 0);
    }
  } else {
    let maxDt: Date | null = null;
    for (const pid of cfg.getPredecessorIds(id)) {
      const candidateDt = cfg.resolvePredecessor(pid);
      if (candidateDt && (!maxDt || candidateDt > maxDt)) maxDt = candidateDt;
    }
    result = maxDt ?? cfg.fallback;
    if (cfg.applyEarliestStart) result = cfg.applyEarliestStart(id, result);
  }
  visiting.delete(id);
  cache[id] = result;
  return result;
}

/**
 * An alwaysOn/is24_5 task can start or finish at any real clock moment, including
 * a weekend or other non-working day — but a REGULAR task (or anything needing a
 * position in the shared, workdays-only Gantt column grid) can't land there. Given
 * such a real clock moment, returns the Date (midnight) of the next AVAILABLE work
 * day to snap forward to, or null when `dt` already falls inside a usable, already-
 * in-progress work window (an actual work day, strictly between dayStartHour and
 * dayStartHour+hoursPerDay) and the caller should keep using dt's own time-of-day.
 *
 * Checking only the hour-of-day — as this function's 3 call sites used to do,
 * independently of each other — misses exactly the case this exists for: the hour
 * can look like a normal business hour (e.g. 11:00) while the calendar DAY itself
 * isn't a work day at all, which an is24h predecessor can legitimately land on
 * since it runs every day of the week. Confirmed live: a regular (non-is24h) task
 * chained right after a 24/7 predecessor whose own end fell on a Sunday at 11:00
 * got scheduled to START before that predecessor's end, because the Sunday 11:00
 * end was never recognized as needing to wait for the next work morning.
 */
function nextWorkMorningIfOutsideWindow(
  dt: Date, dayStartHour: number, hoursPerDay: number, workDays: number[], nonWorkingDays: string[],
): Date | null {
  const nwSet = new Set(nonWorkingDays);
  const ds = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const h = dt.getHours() + dt.getMinutes() / 60;
  const dayIsWork = workDays.includes(dt.getDay()) && !nwSet.has(ds(dt));
  if (dayIsWork && h > dayStartHour && h < dayStartHour + hoursPerDay) return null;
  const next = new Date(dt);
  if (dayIsWork && h >= dayStartHour + hoursPerDay) next.setDate(next.getDate() + 1);
  next.setHours(0, 0, 0, 0);
  while (!workDays.includes(next.getDay()) || nwSet.has(ds(next))) next.setDate(next.getDate() + 1);
  return next;
}

export function workDaysBetween(
  from: Date,
  to: Date,
  workHoursOnly: boolean,
  nonWorkingDays: string[],
  workDays: number[] = [1,2,3,4,5],
): number {
  if (!workHoursOnly) return Math.round((to.getTime() - from.getTime()) / 86400000);
  const nwSet = new Set(nonWorkingDays);
  const sign = to.getTime() >= from.getTime() ? 1 : -1;
  let count = 0;
  const cur = new Date(from); cur.setHours(0, 0, 0, 0);
  const tgt = new Date(to);   tgt.setHours(0, 0, 0, 0);
  while (Math.abs(cur.getTime() - tgt.getTime()) >= 43200000) {
    cur.setDate(cur.getDate() + sign);
    const dow = cur.getDay();
    const ds = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
    if (workDays.includes(dow) && !nwSet.has(ds)) count += sign;
  }
  return count;
}

/**
 * Schedule column 0 always means "the first real work moment of the project" —
 * but several places independently anchor their own day-0 arithmetic at the
 * raw `startDate` (== gantt_start_date) itself, without checking whether that
 * date is actually a work day. `gantt_start_date` landing on a weekend is a
 * real, hit-in-production case (the project-wide backfill once did exactly
 * this to several flowcharts — see CLAUDE.md). When it happens, anchoring at
 * the raw (invalid) date instead of this desyncs every is24h real-time walk
 * and every scheduledStart pin by exactly one work day, in OPPOSITE
 * directions (colToDate/colToRealDate's forward day-walk under-counts;
 * calcStart's/vStart24's scheduledStart-pin formula, via workDaysBetween,
 * over-counts) — confirmed on a real flowchart with a Sunday
 * gantt_start_date, where the two mismatched conventions fragmented the
 * critical path into disconnected islands instead of one continuous chain
 * instead of cancelling out. Use this SAME anchor everywhere "day 0" needs a
 * concrete Date, instead of re-deriving it per call site.
 */
export function firstValidDay0(startDate: Date, workHoursOnly: boolean, nonWorkingDays: string[], workDays: number[] = [1,2,3,4,5]): Date {
  const d = new Date(startDate); d.setHours(0, 0, 0, 0);
  if (!workHoursOnly) return d;
  const nwSet = new Set(nonWorkingDays);
  const toDs = (dt: Date) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  while (!workDays.includes(d.getDay()) || nwSet.has(toDs(d))) d.setDate(d.getDate() + 1);
  return d;
}

/**
 * Calculates the number of working hours between two datetimes, respecting
 * 24/7, 24/5, fullDay, and normal (9–17 weekday) scheduling modes.
 */
export function workHoursBetween(
  start: Date,
  end: Date,
  alwaysOn: boolean,
  is24_5: boolean,
  fullDaySchedule: boolean,
  nonWorkingDays: string[] = [],
  workDays: number[] = [1, 2, 3, 4, 5],
  dayStartH: number = 9,
  hpd: number = 8,
): number {
  if (end.getTime() <= start.getTime()) return 0;
  if (alwaysOn) return (end.getTime() - start.getTime()) / 3600000;

  const is24h = is24_5 || fullDaySchedule;
  const nwSet = new Set(nonWorkingDays);
  const ds = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  let total = 0;
  const cur = new Date(start);

  while (cur < end) {
    const dow = cur.getDay();
    const dateKey = ds(cur);
    if (!workDays.includes(dow) || nwSet.has(dateKey)) {
      cur.setDate(cur.getDate() + 1); cur.setHours(0, 0, 0, 0); continue;
    }
    const nextMidnight = new Date(cur); nextMidnight.setDate(nextMidnight.getDate() + 1); nextMidnight.setHours(0, 0, 0, 0);
    let slotStart: Date, slotEnd: Date;
    if (is24h) {
      slotStart = new Date(cur); slotStart.setHours(0, 0, 0, 0);
      slotEnd = nextMidnight;
    } else {
      slotStart = new Date(cur); slotStart.setHours(dayStartH, 0, 0, 0);
      slotEnd = new Date(cur); slotEnd.setHours(dayStartH + hpd, 0, 0, 0);
    }
    const from = Math.max(cur.getTime(), slotStart.getTime());
    const to = Math.min(end.getTime(), slotEnd.getTime());
    if (to > from) total += (to - from) / 3600000;
    cur.setDate(cur.getDate() + 1); cur.setHours(0, 0, 0, 0);
  }
  return total;
}

export function calcEarliestStartOffset(
  box: ScheduleBox,
  startDate: Date,
  durationUnit: string,
  fullDaySchedule: boolean,
  workHoursOnly: boolean,
  nonWorkingDays: string[],
  workDays: number[] = [1,2,3,4,5],
  dayStartHour = 9,
  hoursPerDay = 8,
): number {
  if (!box.earliestStart) return 0;
  const hpd = fullDaySchedule ? 24 : hoursPerDay;

  if (/^W\d+$/.test(box.earliestStart)) {
    const weekNum = parseInt(box.earliestStart.slice(1));
    if (isNaN(weekNum)) return 0;
    const year = startDate.getFullYear();
    const jan4 = new Date(year, 0, 4);
    const startOfWeek1 = new Date(jan4);
    startOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
    const targetMonday = new Date(startOfWeek1);
    targetMonday.setDate(startOfWeek1.getDate() + (weekNum - 1) * 7);
    const diffCalWeeks = Math.ceil((targetMonday.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
    if (durationUnit === 'hours') return Math.max(0, diffCalWeeks * 5 * hpd);
    if (durationUnit === 'weeks') return Math.max(0, diffCalWeeks);
    return Math.max(0, diffCalWeeks * 5);
  }

  const parts = box.earliestStart.split('T')[0].split('-').map(Number);
  if (parts.length < 3) return 0;
  const target = new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0);
  if (isNaN(target.getTime())) return 0;
  const base = firstValidDay0(startDate, workHoursOnly, nonWorkingDays, workDays);
  const dayDiff = workDaysBetween(base, target, workHoursOnly, nonWorkingDays, workDays);
  if (durationUnit === 'hours') {
    const dayStartH = fullDaySchedule ? 0 : dayStartHour;
    const tp = box.earliestStart.includes('T') ? box.earliestStart.split('T')[1] : '';
    let hourOff = 0;
    if (tp) {
      const [h, m] = tp.split(':').map(Number);
      hourOff = Math.max(0, Math.min(hpd, (h || 0) + (m || 0) / 60 - dayStartH));
    }
    return Math.max(0, dayDiff * hpd + hourOff);
  }
  if (durationUnit === 'weeks') return Math.max(0, dayDiff / 5);
  return Math.max(0, dayDiff);
}

// ── Main export ──────────────────────────────────────────────────────────────

export function calcSchedule<T extends ScheduleBox>(
  boxes: T[],
  links: ScheduleLink[],
  options: ScheduleOptions = {},
): Array<Omit<T, 'duration'> & { start: number; duration: number; endColForConflict: number }> {
  const {
    durationUnit = 'days',
    fullDaySchedule = false,
    workHoursOnly = false,
    workDays = [1,2,3,4,5],
    nonWorkingDays = [],
    startDate = new Date(),
    dayStartHour = 9,
    hoursPerDay = 8,
  } = options;

  // A box with subitems gets its duration from applySubitemRollup instead of its own
  // `duration` string — already in schedule-column units, so parseDuration is skipped
  // entirely for it rather than round-tripped through a formatted string (which
  // parseDuration's is24h compression math can't always decode back exactly).
  const getDur = (bx: ScheduleBox, alwaysOnFlag: boolean, is24_5Flag: boolean): number =>
    bx.rolledUpDuration !== undefined
      ? bx.rolledUpDuration
      : parseDuration(bx.duration, alwaysOnFlag, durationUnit, fullDaySchedule, is24_5Flag, hoursPerDay);

  const calloutIds = new Set(boxes.filter(b => b.type === 'callout').map(b => b.id));
  const deps: Record<number, number[]> = {};
  links.forEach(a => {
    if (calloutIds.has(a.fromBox) || calloutIds.has(a.toBox)) return;
    if (!deps[a.toBox]) deps[a.toBox] = [];
    deps[a.toBox].push(a.fromBox);
  });

  const starts: Record<number, number> = {};
  const visiting = new Set<number>();

  const calcStart = (id: number): number => {
    if (starts[id] !== undefined) return starts[id];
    if (visiting.has(id)) return 0; // cycle guard
    visiting.add(id);

    const thisBox = boxes.find(b => b.id === id);

    // Pinned scheduled date overrides dependency chain (can be negative / in the past)
    if (thisBox?.scheduledStart) {
      const [sy, sm, sd] = thisBox.scheduledStart.split('T')[0].split('-').map(Number);
      const target = new Date(sy, sm - 1, sd, 0, 0, 0);
      const base = firstValidDay0(startDate, workHoursOnly, nonWorkingDays, workDays);
      const dayDiff = workDaysBetween(base, target, workHoursOnly, nonWorkingDays, workDays);
      const hpd = fullDaySchedule ? 24 : hoursPerDay;
      let offset: number;
      if (durationUnit === 'hours') {
        const boxIs24h = thisBox?.alwaysOn || thisBox?.is24_5;
        const dayStartH = (fullDaySchedule || boxIs24h) ? 0 : dayStartHour;
        const effectiveHpd = (fullDaySchedule || boxIs24h) ? 24 : hpd;
        const tp = thisBox.scheduledStart.includes('T') ? thisBox.scheduledStart.split('T')[1] : '';
        let hourOff = 0;
        if (tp) {
          const [h, m] = tp.split(':').map(Number);
          hourOff = Math.max(0, Math.min(effectiveHpd, (h || 0) + (m || 0) / 60 - dayStartH));
        }
        offset = dayDiff * hpd + hourOff;
      } else if (durationUnit === 'weeks') {
        offset = dayDiff / 5;
      } else {
        offset = dayDiff;
      }
      visiting.delete(id);
      starts[id] = offset;
      return offset;
    }

    const preds = deps[id] || [];
    let mx: number;
    if (!preds.length) {
      mx = 0;
    } else {
      mx = Math.max(...preds.map(p => {
        const ps = calcStart(p);
        const pb = boxes.find(b => b.id === p);
        if (pb?.type === 'startday') return parseInt(pb.text ?? '0') || 0;

        // alwaysOn / is24_5 → regular in hours mode: snap successor to next work morning.
        const predIs24h = pb?.alwaysOn || pb?.is24_5;
        const thisIs24h = thisBox?.alwaysOn || thisBox?.is24_5;
        if (predIs24h && !thisIs24h && !fullDaySchedule && durationUnit === 'hours') {
          const hpdCS = hoursPerDay; const dayStartCS = dayStartHour;
          // The predecessor's real clock start/end must come from getIs24hRealStart/
          // getIs24hRealEnd, which walk the whole is24h ancestor chain in real time
          // (same logic as getAlwaysOnClockStart in FlowchartBuilder.tsx). Decoding
          // it from ps instead is only valid for a lone is24h task anchored directly
          // to the project start or a scheduledStart pin: ps lives in a column space
          // where "8 units" means one calendar day for an is24h chain (24 real hours
          // compressed 3:1), but such a decode always assumes "8 units" is an 8-hour,
          // 9am-5pm workday — so any predecessor whose own start was itself derived
          // from an earlier is24h hop (a chain of two or more 24/5 tasks) would
          // decode to the wrong clock time, an error that compounds with every hop.
          const endClock = getIs24hRealEnd(p);
          const next = nextWorkMorningIfOutsideWindow(endClock, dayStartCS, hpdCS, workDays, nonWorkingDays);
          if (next) {
            const base = firstValidDay0(startDate, workHoursOnly, nonWorkingDays, workDays);
            return workDaysBetween(base, next, workHoursOnly, nonWorkingDays, workDays) * hpdCS;
          }
          // Ends within work hours → return work-hour column.
          // No rounding here: endH can legitimately fall on a non-whole hour
          // (e.g. a 24/7 predecessor finishing at 14:30), and rounding it to the
          // nearest hour column desyncs the successor's scheduled start from the
          // predecessor's own displayed end time (getEnd/getAlwaysOnClockStart in
          // FlowchartBuilder.tsx use exact clock arithmetic, no rounding) — see
          // the same rationale on dateToHourCol below.
          const endH = endClock.getHours() + endClock.getMinutes() / 60;
          const endMid = new Date(endClock); endMid.setHours(0, 0, 0, 0);
          const base2 = firstValidDay0(startDate, workHoursOnly, nonWorkingDays, workDays);
          return workDaysBetween(base2, endMid, workHoursOnly, nonWorkingDays, workDays) * hpdCS + Math.max(0, endH - dayStartCS);
        }

        return ps + (pb ? getDur(pb, pb.alwaysOn ?? false, pb.is24_5 ?? false) : 1);
      }));
    }

    if (thisBox) mx = Math.max(mx, calcEarliestStartOffset(thisBox, startDate, durationUnit, fullDaySchedule, workHoursOnly, nonWorkingDays, workDays, dayStartHour, hoursPerDay));

    visiting.delete(id);
    starts[id] = mx;
    return mx;
  };

  // Converts a REGULAR (non-is24h) task's schedule column into a real clock Date,
  // using the standard 9am-5pm/8h-per-day grid — the only convention regular
  // columns ever use, so this decode is always exact for them.
  const colToRealDate = (col: number): Date => {
    const hpdCS = hoursPerDay; const dayStartCS = dayStartHour;
    const nwSet = new Set(nonWorkingDays);
    const totalMin = Math.round(col * 60);
    const hpdMin = hpdCS * 60;
    const days = Math.floor(totalMin / hpdMin);
    const remMin = totalMin % hpdMin;
    const clock = firstValidDay0(startDate, workHoursOnly, nonWorkingDays, workDays);
    let added = 0;
    while (added < days) {
      clock.setDate(clock.getDate() + 1);
      if (workHoursOnly) {
        const dow = clock.getDay();
        const ds = `${clock.getFullYear()}-${String(clock.getMonth() + 1).padStart(2, '0')}-${String(clock.getDate()).padStart(2, '0')}`;
        if (!workDays.includes(dow) || nwSet.has(ds)) continue;
      }
      added++;
    }
    clock.setHours(dayStartCS + Math.floor(remMin / 60), remMin % 60, 0, 0);
    return clock;
  };

  // Real (unsnapped) clock start/end of an is24h (alwaysOn/is24_5) box, walking its
  // whole is24h ancestor chain in real time — mirrors getAlwaysOnClockStart in
  // FlowchartBuilder.tsx / computeVisualPositions' getIs24hRealStart below. Needed
  // because a chain of two or more is24h boxes accumulates schedule-column values
  // (via the "ps + duration" fallback a few lines up) that use a "8 units = 1
  // calendar day" scale specific to is24h chains; decoding an intermediate hop's
  // raw column value through the standard 9am/8h regular-task grid (colToRealDate)
  // gives the wrong clock time, and the error compounds with every extra hop.
  const is24hRealStartCache: Record<number, Date> = {};
  const is24hRealEndCache: Record<number, Date> = {};
  const is24hVisiting = new Set<number>();
  const getIs24hRealStart = (id: number): Date => walkIs24hRealStart(id, is24hRealStartCache, is24hVisiting, {
    getScheduledStart: (bid) => boxes.find(b => b.id === bid)?.scheduledStart,
    scheduledStartDefaultHour: 0,
    fallback: firstValidDay0(startDate, workHoursOnly, nonWorkingDays, workDays),
    getPredecessorIds: (bid) => deps[bid] || [],
    resolvePredecessor: (pid) => {
      const pb = boxes.find(b => b.id === pid);
      if (!pb || pb.type === 'startday') return null;
      return (pb.alwaysOn || pb.is24_5)
        ? getIs24hRealEnd(pid)
        : colToRealDate(calcStart(pid) + getDur(pb, pb.alwaysOn ?? false, pb.is24_5 ?? false));
    },
    // Without this, an is24h box's own reported `start` column (via calcStart's
    // separate calcEarliestStartOffset clamp, a few lines up) honors its earliestStart
    // pin, but the real-clock chain value computed HERE — used when this box feeds a
    // REGULAR successor via the predIs24h branch below — did not, since it only ever
    // looked at predecessors. A predecessor-derived real time earlier than the pin
    // (e.g. a predecessor finishing well before the earliestStart date) then let the
    // regular successor's start be derived from that too-early, unclamped real end,
    // landing before the predecessor's own officially reported end. Mirrors
    // computeVisualPositions' own applyEarliestStart below exactly, including only
    // honoring the date part (not an explicit time-of-day) — same existing convention.
    applyEarliestStart: (bid, result) => {
      const t = boxes.find(b => b.id === bid);
      if (t?.earliestStart && !t.earliestStart.startsWith('W')) {
        const [y, m, d] = t.earliestStart.split('T')[0].split('-').map(Number);
        const esDt = new Date(y, m - 1, d, dayStartHour, 0, 0);
        if (esDt > result) return esDt;
      }
      return result;
    },
  });
  const getIs24hRealEnd = (id: number): Date => {
    if (is24hRealEndCache[id]) return is24hRealEndCache[id];
    const box = boxes.find(b => b.id === id)!;
    const s = getIs24hRealStart(id);
    const dur = getDur(box, true, false);
    const result = is24hEndFromStart(s, dur, box.is24_5 ?? false, nonWorkingDays, workDays);
    is24hRealEndCache[id] = result;
    return result;
  };

  // For alwaysOn/is24_5 tasks in hours mode the Gantt duration (clock hours) can span
  // past a day boundary. For conflict detection we need the "next work morning" column
  // rather than start+duration (which can far overshoot the scheduled successor start).
  const calcEndColForConflict = (b: ScheduleBox, s: number, dur: number): number => {
    const predIs24h = b.alwaysOn || b.is24_5;
    if (!predIs24h || fullDaySchedule || durationUnit !== 'hours') return s + dur;
    const hpdCS = hoursPerDay; const dayStartCS = dayStartHour;
    // Real clock end via getIs24hRealEnd (walks the whole is24h ancestor chain in
    // real time) — see the matching comment in calcStart above for why decoding it
    // from s instead would be wrong for a box whose own start was itself derived
    // from an earlier is24h hop.
    const endClock = getIs24hRealEnd(b.id);
    const next = nextWorkMorningIfOutsideWindow(endClock, dayStartCS, hpdCS, workDays, nonWorkingDays);
    if (next) {
      const base = firstValidDay0(startDate, workHoursOnly, nonWorkingDays, workDays);
      return workDaysBetween(base, next, workHoursOnly, nonWorkingDays, workDays) * hpdCS;
    }
    // No rounding here — see the matching comment in calcStart above.
    const endH = endClock.getHours() + endClock.getMinutes() / 60;
    const endMid = new Date(endClock); endMid.setHours(0, 0, 0, 0);
    const base2 = firstValidDay0(startDate, workHoursOnly, nonWorkingDays, workDays);
    return workDaysBetween(base2, endMid, workHoursOnly, nonWorkingDays, workDays) * hpdCS + Math.max(0, endH - dayStartCS);
  };

  return boxes.map(b => {
    const s = calcStart(b.id);
    const dur = b.type === 'startday' ? 0 : getDur(b, b.alwaysOn ?? false, b.is24_5 ?? false);
    return {
      ...b,
      start: s,
      duration: dur,
      endColForConflict: b.type === 'startday' ? s : calcEndColForConflict(b, s, dur),
    };
  });
}

// ── Subitems (nested boxes) ────────────────────────────────────────────────
// Not yet wired into the live app — see CLAUDE.md's note on Box.parentBoxId. Building
// and testing this in isolation first, per the same rule as everything else in this
// file: new is24h/scheduling logic always goes through the one shared engine, never
// re-derived at a call site.

/**
 * A subitem inherits its parent's alwaysOn/is24_5 flag whenever the subitem doesn't
 * set its own — Box.alwaysOn/is24_5 are plain optional booleans with no way to
 * express "explicitly off" vs. "unset" (see ScheduleBox above), and the
 * SubitemsPanel UI has no control for either field on a subitem at all, so every
 * subitem's own flag is always unset in practice; a truthy value on the subitem
 * itself still wins, same convention as every other optional Box field. Without
 * this, a 24/5 (or 24/7) parent's subitems schedule under ordinary work-hours-only
 * rules and visually fragment at every non-working-hour boundary, even though the
 * parent box itself runs continuously.
 */
function inheritIs24h<T extends ScheduleBox>(parent: T | undefined, subitems: T[]): T[] {
  if (!parent || !(parent.alwaysOn || parent.is24_5)) return subitems;
  return subitems.map(s => (s.alwaysOn || s.is24_5) ? s : { ...s, alwaysOn: parent.alwaysOn, is24_5: parent.is24_5 });
}

/**
 * Computes the rolled-up duration of a box from its subitems — boxes whose
 * parentBoxId points to it — by scheduling those subitems as their own independent
 * chain (a fresh calcSchedule call, scoped to just that subset) and measuring the
 * span from the earliest subitem start to the latest subitem end. Returned in the
 * same schedule-column units as `options.durationUnit` — ready to assign straight to
 * a box's `rolledUpDuration` field, no unit conversion needed.
 *
 * Returns null when the box has no subitems, so the caller can tell "nothing to roll
 * up, leave this box's own duration alone" apart from a genuine (if unusual) zero-span
 * result from real subitems.
 */
export function calcSubitemDuration<T extends ScheduleBox>(
  parentId: number,
  allBoxes: T[],
  allLinks: ScheduleLink[],
  options: ScheduleOptions = {},
): number | null {
  const subitems = allBoxes.filter(b => b.parentBoxId === parentId);
  if (subitems.length === 0) return null;
  const subIds = new Set(subitems.map(b => b.id));
  const subLinks = allLinks.filter(l => subIds.has(l.fromBox) && subIds.has(l.toBox));
  const parent = allBoxes.find(b => b.id === parentId);
  const scheduled = calcSchedule(inheritIs24h(parent, subitems), subLinks, options);
  const starts = scheduled.map(t => t.start);
  const ends = scheduled.map(t => t.start + t.duration);
  return Math.max(...ends) - Math.min(...starts);
}

/**
 * Like calcSubitemDuration, but returns each subitem's own ScheduledTask instead of
 * just the aggregate span — for displaying subitems as their own indented rows
 * directly under their parent's row in the Gantt (as opposed to the single rolled-up
 * bar applySubitemRollup/calcSubitemDuration produce). Reuses calcSchedule's own local
 * (subitem-only) schedule, then shifts every result by
 * (parentAbsoluteStart - localGroupStart) so the subitems land at their true absolute
 * position in the outer schedule, directly under the parent's actual bar, instead of
 * at the local group's own column 0.
 *
 * Returns [] when the box has no subitems.
 */
export function calcSubitemGanttTasks<T extends ScheduleBox>(
  parentId: number,
  parentAbsoluteStart: number,
  allBoxes: T[],
  allLinks: ScheduleLink[],
  options: ScheduleOptions = {},
): Array<Omit<T, 'duration'> & { start: number; duration: number; endColForConflict: number }> {
  const subitems = allBoxes.filter(b => b.parentBoxId === parentId);
  if (subitems.length === 0) return [];
  const subIds = new Set(subitems.map(b => b.id));
  const subLinks = allLinks.filter(l => subIds.has(l.fromBox) && subIds.has(l.toBox));
  const parent = allBoxes.find(b => b.id === parentId);
  const scheduled = calcSchedule(inheritIs24h(parent, subitems), subLinks, options);
  const localGroupStart = Math.min(...scheduled.map(t => t.start));
  const shift = parentAbsoluteStart - localGroupStart;
  return scheduled.map(t => ({ ...t, start: t.start + shift }));
}

/**
 * Returns a copy of `boxes` where every box that has subitems gets a
 * `rolledUpDuration` computed from them (see calcSubitemDuration) — calcSchedule uses
 * this instead of parsing the box's own `duration` string wherever it's present.
 * Boxes without subitems (i.e. every box today, since nothing sets parentBoxId yet)
 * come back completely unchanged — this is a no-op until the subitems feature's UI
 * actually creates some.
 *
 * Call this BEFORE calcSchedule (on the same full boxes/links the outer schedule will
 * use) so the rollup participates in the outer schedule exactly like a manually-set
 * duration would — dependency chains, critical path, and conflict detection all keep
 * working unchanged, they just see a box whose duration happens to come from its
 * subitems instead of its own duration field.
 */
export function applySubitemRollup<T extends ScheduleBox>(
  boxes: T[],
  links: ScheduleLink[],
  options: ScheduleOptions = {},
): T[] {
  return boxes.map(b => {
    const rolled = calcSubitemDuration(b.id, boxes, links, options);
    return rolled === null ? b : { ...b, rolledUpDuration: rolled };
  });
}

// ── Area grouping (for the Gantt's Area-based "toplevel" rows) ───────────────

/**
 * Assigns each box to at most one Area, for grouping boxes into an Area-based
 * "toplevel" row in the Gantt (see GanttPanel.tsx). A box belongs to an area if
 * their rectangles overlap at all — full containment isn't required, a box
 * straddling an area's boundary still counts, matching how areas are used
 * visually on the canvas (a loose grouping aid, not a strict container). When a
 * box overlaps more than one area, the area with the largest overlap wins.
 * Boxes that don't overlap any area are simply absent from the returned map.
 *
 * Pure geometry, not scheduling — grouped here anyway (rather than e.g.
 * arrowRouting.ts) for consistency with calcSubitemGanttTasks's own role: all
 * "boxes → Gantt row structure" logic lives in this one file.
 */
export function assignBoxesToAreas(
  boxes: Array<{ id: number; x: number; y: number; width: number; height: number }>,
  areas: Array<{ id: number; x: number; y: number; width: number; height: number }>,
): Map<number, number> {
  const result = new Map<number, number>();
  for (const box of boxes) {
    let bestAreaId: number | null = null;
    let bestOverlap = 0;
    for (const area of areas) {
      const overlapW = Math.min(box.x + box.width, area.x + area.width) - Math.max(box.x, area.x);
      const overlapH = Math.min(box.y + box.height, area.y + area.height) - Math.max(box.y, area.y);
      if (overlapW <= 0 || overlapH <= 0) continue; // no overlap (or touching only)
      const overlapArea = overlapW * overlapH;
      if (overlapArea > bestOverlap) {
        bestOverlap = overlapArea;
        bestAreaId = area.id;
      }
    }
    if (bestAreaId !== null) result.set(box.id, bestAreaId);
  }
  return result;
}

/**
 * An Area-toplevel Gantt row's own completion % — the duration-weighted average
 * of its member boxes' completion, so a 10-day task's progress counts 10x as much
 * toward the Area's overall % as a 1-day task's, regardless of whether they run in
 * parallel or series. Shared by GanttPanel.tsx and lib/svgExport.ts's own
 * makeAreaHeaderTask callbacks (passed to buildExpandedGanttRows below) — previously
 * both hardcoded 0, so an Area header never showed any progress at all.
 */
export function calcAreaCompletion(members: Array<{ completion?: number; duration: number }>): number {
  const totalDuration = members.reduce((sum, m) => sum + m.duration, 0);
  if (totalDuration <= 0) return 0;
  const weighted = members.reduce((sum, m) => sum + (m.completion ?? 0) * m.duration, 0);
  return Math.round(weighted / totalDuration);
}

/**
 * Builds the flat, ordered row list a Gantt view actually renders — interleaving
 * each top-level task's subitem rows directly after it, and/or grouping tasks into
 * their Area's toplevel row (see assignBoxesToAreas above) — from the SAME rules,
 * for BOTH the live GanttPanel view and the static SVG/PNG export. Before this
 * existed the two had independent copies of this grouping logic; keeping it in one
 * place means a future change to how subitems/areas interleave can't accidentally
 * apply to only one of them (exactly the failure mode that motivated centralizing
 * is24h scheduling math here in the first place — see this file's own header note).
 *
 * Deliberately generic over the task shape and deliberately NOT responsible for
 * subitem coordinate positioning or area-header construction — callers differ there
 * (GanttPanel's live view re-anchors subitems into its own calendarOrigin-relative
 * "adjusted" coordinate space and needs a Box-shaped area-header object; the export
 * works entirely in calcSchedule's raw ganttStartDate-relative space and needs its
 * own task shape) — `getSubitemsFor`/`makeAreaHeaderTask` let each caller supply
 * exactly what it needs while sharing the one thing that must never drift apart:
 * the actual grouping/ordering decisions.
 *
 * `topLevelIndexById`/`expandedIndexForTopLevelIndex`-style bookkeeping (needed only
 * by GanttPanel's interactive drag-to-reorder) is deliberately NOT this function's
 * concern — derive that from its return value if needed, same as any other consumer.
 */
export function buildExpandedGanttRows<T extends { id: number }>(params: {
  topLevelTasks: T[];
  areaByBoxId: Map<number, number>;
  getSubitemsFor: (parent: T) => T[];
  makeAreaHeaderTask: (areaId: number, members: T[]) => T | null;
  includeSubitems: boolean;
  includeAreaGroups: boolean;
  includeRegularBoxes: boolean;
}): T[] {
  const { topLevelTasks, areaByBoxId, getSubitemsFor, makeAreaHeaderTask, includeSubitems, includeAreaGroups, includeRegularBoxes } = params;
  if (!includeSubitems && !includeAreaGroups && includeRegularBoxes) return topLevelTasks;

  const expanded: T[] = [];
  const consumed = new Set<number>();
  // includeRegularBoxes off + includeAreaGroups on = "only toplevel": a task (and its
  // subitems) is skipped entirely rather than rendered, but still marked consumed so
  // it isn't reconsidered as its own group below.
  const pushTask = (t: T) => {
    if (includeRegularBoxes) {
      expanded.push(t);
      if (includeSubitems) expanded.push(...getSubitemsFor(t));
    }
    consumed.add(t.id);
  };
  topLevelTasks.forEach(t => {
    if (consumed.has(t.id)) return; // already emitted as part of an earlier area group
    const areaId = includeAreaGroups ? areaByBoxId.get(t.id) : undefined;
    if (areaId === undefined) { pushTask(t); return; }
    // First task of this area encountered — flush the WHOLE group now (in their
    // relative order) so same-area tasks render contiguously under one header,
    // instead of scattered wherever they'd otherwise fall.
    const members = topLevelTasks.filter(m => !consumed.has(m.id) && areaByBoxId.get(m.id) === areaId);
    const header = makeAreaHeaderTask(areaId, members);
    if (header) expanded.push(header);
    members.forEach(pushTask);
  });
  return expanded;
}

// ── Derived analytics (pure, depend only on calcSchedule output) ─────────────

/**
 * Returns the set of box IDs and arrow IDs on the critical path.
 * Uses the Late-Finish method: a task is critical when LS ≈ ES (slack = 0).
 * Callout and startday boxes are excluded from the path.
 *
 * Pass `visualPositions` (from computeVisualPositions) so the CP uses the
 * calendar-based bar widths instead of the raw scheduling durations —
 * important for 24/7 and 24/5 tasks in days mode.
 *
 * Project end (the anchor every task's slack is measured against) is the
 * latest finish among tasks marked `importantFinish` (a milestone), if any
 * exist — otherwise the latest finish among all connected tasks. See the
 * comment at `milestoneTasks` below for why this matters.
 */
export function calcCriticalPath(
  tasks: ScheduledTask[],
  links: ScheduleLink[],
  visualPositions?: VisualPositions,
): { boxIds: Set<number>; arrowIds: Set<number> } {
  const boxIds = new Set<number>();
  const arrowIds = new Set<number>();

  const cpTasks = tasks.filter(t => t.type !== 'callout' && t.type !== 'startday');
  if (cpTasks.length === 0) return { boxIds, arrowIds };

  const cpIds = new Set(cpTasks.map(t => t.id));
  const durMap: Record<number, number> = {};
  const startMap: Record<number, number> = {};
  cpTasks.forEach(t => {
    const vp = visualPositions?.positions.get(t.id);
    durMap[t.id]  = t.duration === 0 ? 0 : (vp?.duration ?? t.duration);
    startMap[t.id] = vp?.start    ?? t.start;
  });

  // Build successor adjacency (through non-task nodes, skipping callout connections)
  const calloutIdsCP = new Set(tasks.filter(t => t.type === 'callout').map(t => t.id));
  // A task with a pinned scheduledStart ignores its predecessors entirely (calcStart
  // overrides the dependency chain for it) — so an incoming edge into one is
  // informational, not a real scheduling constraint: a predecessor only needs to
  // finish before the pin, not "just in time" for it. This used to be handled by
  // dropping the edge entirely, which went too far — on a graph with several pins
  // scattered around, a predecessor could lose its only real downstream constraint
  // this way and fall back to the (unrelated, often much shorter) project end, which
  // corrupts calcLF for everything upstream of it too, up to the point of finding
  // zero critical tasks at all on some real projects. Instead, keep the edge and let
  // calcLF (below) use the pin's own fixed start as that predecessor's upper bound.
  const pinnedIds = new Set(cpTasks.filter(t => t.scheduledStart).map(t => t.id));
  const suppressedEdges = visualPositions?.suppressedEdges;
  const adjSuccs: Record<number, number[]> = {};
  links.forEach(a => {
    if (calloutIdsCP.has(a.fromBox) || calloutIdsCP.has(a.toBox)) return;
    if (suppressedEdges?.has(`${a.fromBox}->${a.toBox}`)) return;
    if (!adjSuccs[a.fromBox]) adjSuccs[a.fromBox] = [];
    adjSuccs[a.fromBox].push(a.toBox);
  });
  const findSuccs = (id: number, visited = new Set<number>()): number[] => {
    if (visited.has(id)) return [];
    visited.add(id);
    const result: number[] = [];
    for (const nextId of (adjSuccs[id] || [])) {
      if (cpIds.has(nextId)) result.push(nextId);
      else for (const s of findSuccs(nextId, visited)) result.push(s);
    }
    return [...new Set(result)];
  };
  const succs: Record<number, number[]> = {};
  cpTasks.forEach(t => { succs[t.id] = findSuccs(t.id); });

  // Build predecessor set to identify isolated (disconnected) tasks
  const hasPred = new Set<number>();
  const hasSuccCP = new Set<number>();
  links.forEach(a => {
    if (calloutIdsCP.has(a.fromBox) || calloutIdsCP.has(a.toBox)) return;
    if (cpIds.has(a.fromBox)) hasSuccCP.add(a.fromBox);
    if (cpIds.has(a.toBox)) hasPred.add(a.toBox);
  });
  // Exclude isolated tasks (no predecessors AND no successors) from projectEnd
  // so they don't inflate the project duration and mask the real critical path.
  const connectedCpTasks = cpTasks.filter(t => hasPred.has(t.id) || hasSuccCP.has(t.id));
  // User-marked milestones (Box.importantFinish — the canvas/Gantt purple diamond
  // badge) take priority over the "longest connected task" fallback below when any
  // exist. Without this, a long-running task with a predecessor but NO successor
  // (e.g. an open-ended "Defect Management"/"Cutover Management" tracking task that
  // nothing downstream actually depends on finishing) still counts as "connected"
  // and, if it happens to be the single longest-finishing one, silently hijacks
  // projectEnd for the WHOLE graph — every other chain then measures its slack
  // against that irrelevant task's finish instead of the real deliverable the user
  // cares about, fragmenting what should be one continuous critical path into
  // disconnected islands (found on a real project where this masked the entire
  // intended chain from a "S4 backup" milestone through to "Validations
  // sign-off/Exit meeting", both explicitly marked importantFinish). When the user
  // has marked at least one task as a milestone, project end is defined as the
  // latest of those — not the single longest leaf anywhere in the graph.
  const milestoneTasks = cpTasks.filter(t => t.importantFinish);
  const projectEndBase = milestoneTasks.length > 0 ? milestoneTasks
    : connectedCpTasks.length > 0 ? connectedCpTasks : cpTasks;
  const projectEnd = Math.max(...projectEndBase.map(t => startMap[t.id] + durMap[t.id]));
  const milestoneIds = new Set(milestoneTasks.map(t => t.id));
  const lfCache: Record<number, number> = {};
  const calcLF = (id: number): number => {
    if (lfCache[id] !== undefined) return lfCache[id];
    const ts = succs[id] || [];
    return (lfCache[id] = ts.length === 0
      // A dead-end task (no successors) that is itself a milestone genuinely must
      // finish by projectEnd. But once milestones are in play, a dead-end task
      // that ISN'T one has no real deadline at all — it's a side branch nothing
      // downstream depends on, not a second definition of "done". Forcing it to
      // projectEnd anyway would just reintroduce the same hijacking this whole
      // milestone mechanism exists to prevent, one level removed: via Math.min
      // below, it could still drag down the LF of any shared ancestor it
      // happens to share with a real milestone chain. Infinity gives it no real
      // constraint instead, so it never reads as critical and never corrupts a
      // shared ancestor's LF. (When no milestones are marked at all, this branch
      // is unreachable — milestoneIds is empty — so legacy behavior is unchanged.)
      ? (milestoneIds.size > 0 && !milestoneIds.has(id) ? Infinity : projectEnd)
      // A pinned successor's start is fixed regardless of this predecessor — use its
      // actual start as the upper bound directly, instead of recursing into its own
      // LF (which reflects ITS successors, not this edge, and could come from a
      // shorter, unrelated branch).
      : Math.min(...ts.map(sid => pinnedIds.has(sid) ? startMap[sid] : calcLF(sid) - durMap[sid])));
  };
  cpTasks.forEach(t => calcLF(t.id));

  // Tasks where LS ≈ ES have zero slack → critical
  cpTasks.forEach(t => {
    const ls = lfCache[t.id] - durMap[t.id];
    if (Math.abs(ls - startMap[t.id]) < 0.001) boxIds.add(t.id);
  });

  // An arrow is critical only if THIS edge is actually what drives the successor's
  // start — both endpoints being independently critical isn't enough. A box can have
  // several predecessors; only the one whose end exactly reaches the successor's start
  // is the real constraint. (A box can also be critical via a completely different
  // successor than the one this arrow points to.) Without this check, any arrow
  // between two critical boxes lit up red even when a different, unrelated
  // predecessor was the one actually setting the successor's start.
  links.forEach(a => {
    if (!boxIds.has(a.fromBox) || !boxIds.has(a.toBox) || a.id == null) return;
    const predEnd = startMap[a.fromBox] + durMap[a.fromBox];
    if (Math.abs(predEnd - startMap[a.toBox]) < 0.001) arrowIds.add(a.id);
  });

  return { boxIds, arrowIds };
}

/**
 * Computes visual (calendar-based) start and duration for each task.
 * In days mode with work-days-only scheduling, 24/7 and 24/5 tasks have a
 * raw scheduling duration (effort / 24) that differs from their calendar span.
 * This function returns the calendar-span positions that match the Gantt bars,
 * so that calcCriticalPath reflects what the user actually sees.
 *
 * Also handles hours mode: an is24h (alwaysOn/is24_5) task's raw schedule
 * column doesn't map linearly to real time once chained, so this computes its
 * real-clock-based position there too (see the "Hours mode" branch below) —
 * despite what an earlier version of this comment claimed, hours mode does
 * NOT return an empty positions map.
 *
 * Returns an empty Map for modes where raw and visual positions already match
 * (weeks mode, or when all-days scheduling is used).
 */
export function computeVisualPositions(
  tasks: ScheduledTask[],
  links: ScheduleLink[],
  boxes: ScheduleBox[],
  options: ScheduleOptions,
): VisualPositions {
  const {
    durationUnit = 'days',
    fullDaySchedule = false,
    workDays = [1, 2, 3, 4, 5],
    nonWorkingDays = [],
    startDate = new Date(),
    dayStartHour = 9,
    hoursPerDay = 8,
  } = options;

  const positions = new Map<number, { start: number; duration: number }>();
  const schedWHO = workDays.length < 7;

  const nwSet = new Set(nonWorkingDays);
  const toDs = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  // ── Hours mode: compute visual positions for 24h tasks ─────────────────────
  if (durationUnit === 'hours' && schedWHO && !fullDaySchedule) {
    const hpd = hoursPerDay;
    const dayStart = dayStartHour;

    // Convert column s to a clock time (asPosition=true: "what time is column s?")
    const colToDate = (s: number): Date => {
      const d = firstValidDay0(startDate, schedWHO, nonWorkingDays, workDays);
      const totalMin = Math.round(s * 60);
      const hpdMin = hpd * 60;
      const days = Math.floor(totalMin / hpdMin);
      const remMin = totalMin % hpdMin;
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (workDays.includes(d.getDay()) && !nwSet.has(toDs(d))) added++;
      }
      d.setHours(dayStart + Math.floor(remMin / 60), remMin % 60, 0, 0);
      return d;
    };

    // Find the working-day column for a clock time (first column >= that time)
    const dateToHourCol = (dt: Date): number => {
      const base = new Date(startDate); base.setHours(dayStart, 0, 0, 0);
      // Count work-hours from base to dt
      let col = 0;
      const cur = new Date(base);
      while (cur < dt) {
        const dow = cur.getDay();
        const ds = toDs(cur);
        if (!workDays.includes(dow) || nwSet.has(ds)) {
          cur.setDate(cur.getDate() + 1); cur.setHours(dayStart, 0, 0, 0); continue;
        }
        const eod = new Date(cur); eod.setHours(dayStart + hpd, 0, 0, 0);
        // No rounding here: a task ending at e.g. 16:30 (7.5h into the work window)
        // must stay within today's column block (< hpd), not round up to hpd and
        // alias with tomorrow's 9am column — that phantom day-jump was inflating
        // is24_5 chains that happened to end in the last half-hour before 17:00.
        if (dt <= eod) { col += (dt.getTime() - cur.getTime()) / 3600000; break; }
        col += hpd; cur.setDate(cur.getDate() + 1); cur.setHours(dayStart, 0, 0, 0);
      }
      return col;
    };

    const parseIsoH = (s: string): Date => {
      const [y, m, d] = s.split('T')[0].split('-').map(Number);
      return new Date(y, m - 1, d, dayStart, 0, 0);
    };

    const taskMap24 = new Map(tasks.map(t => [t.id, t]));
    const calloutIds24 = new Set(tasks.filter(t => t.type === 'callout').map(t => t.id));
    const deps24: Record<number, number[]> = {};
    links.forEach(a => {
      if (calloutIds24.has(a.fromBox) || calloutIds24.has(a.toBox)) return;
      if (!deps24[a.toBox]) deps24[a.toBox] = [];
      deps24[a.toBox].push(a.fromBox);
    });

    const vStarts24: Record<number, number> = {};
    const vis24 = new Set<number>();
    const is24hVisiting24 = new Set<number>();

    // True (unsnapped) real clock start/end, tracked ONLY for is24h tasks and used
    // exclusively when chaining into an is24h successor. dateToHourCol/colToDate
    // can only represent moments inside the 9am-5pm grid — any evening/night time
    // aliases to "next business day 9am" once round-tripped through them, which is
    // correct for a REGULAR successor (which genuinely can't start outside 9-5)
    // but wrong for an is24h successor (which should continue right away). Regular
    // tasks are untouched here: their start/duration stay in pure column space via
    // vStart24/vEnd24 below, unchanged from before.
    const realStarts24: Record<number, Date> = {};
    const realEnds24: Record<number, Date> = {};

    const getIs24hRealStart = (id: number): Date => walkIs24hRealStart(id, realStarts24, is24hVisiting24, {
      getScheduledStart: (bid) => taskMap24.get(bid)?.scheduledStart,
      scheduledStartDefaultHour: dayStart,
      fallback: colToDate(0), // no predecessors: column 0 = day 0, 9am
      getPredecessorIds: (bid) => deps24[bid] || [],
      resolvePredecessor: (pid) => {
        const pred = taskMap24.get(pid);
        if (!pred) return null;
        if (pred.type === 'startday') return colToDate(parseInt(pred.text ?? '0') || 0);
        const predIs24h = !!(pred.alwaysOn || pred.is24_5 || fullDaySchedule);
        return predIs24h ? getIs24hRealEnd(pid) : colToDate(vEnd24(pid));
      },
      applyEarliestStart: (bid, result) => {
        const t = taskMap24.get(bid)!;
        if (t.earliestStart && !t.earliestStart.startsWith('W')) {
          const esDt = parseIsoH(t.earliestStart);
          if (esDt > result) return esDt;
        }
        return result;
      },
    });

    const getIs24hRealEnd = (id: number): Date => {
      if (realEnds24[id]) return realEnds24[id];
      const t = taskMap24.get(id)!;
      const s = getIs24hRealStart(id);
      const result = is24hEndFromStart(s, t.duration, t.is24_5 ?? false, nonWorkingDays, workDays);
      realEnds24[id] = result;
      return result;
    };

    // Convert a real clock Date into the shared 9am-5pm column grid: if it falls
    // outside business hours, advance to the next available work morning. This is
    // the ONLY place that "wait until 9am" transformation happens now — applied
    // once, when a position needs to be expressed in the shared column space (for
    // display, calcCriticalPath duration/float, or a regular successor's start),
    // not at every intermediate is24h-to-is24h handoff.
    const snapToGridCol = (dt: Date): number => {
      const next = nextWorkMorningIfOutsideWindow(dt, dayStart, hpd, workDays, nonWorkingDays);
      if (next) {
        next.setHours(dayStart, 0, 0, 0);
        return dateToHourCol(next);
      }
      return dateToHourCol(dt);
    };

    const vEnd24 = (id: number): number => {
      const t = taskMap24.get(id);
      if (!t || t.type === 'startday') return vStart24(id);
      const is24h = t.alwaysOn || t.is24_5 || fullDaySchedule;
      if (!is24h) return vStart24(id) + t.duration; // regular tasks: unchanged, pure column arithmetic
      return snapToGridCol(getIs24hRealEnd(id));
    };

    const vStart24 = (id: number): number => {
      if (vStarts24[id] !== undefined) return vStarts24[id];
      if (vis24.has(id)) return 0;
      vis24.add(id);
      const t = taskMap24.get(id);
      const idIs24h = !!(t && (t.alwaysOn || t.is24_5 || fullDaySchedule));
      let result = 0;
      if (idIs24h) {
        result = snapToGridCol(getIs24hRealStart(id));
      } else if (t?.scheduledStart) {
        // Mirror calcStart's own scheduledStart formula (day-block + hourOff since
        // dayStart) instead of dateToHourCol(parseIsoH(...)), which silently drops
        // the time-of-day.
        const [datePart, timePart] = t.scheduledStart.split('T');
        const [sy, sm, sd] = datePart.split('-').map(Number);
        const target = new Date(sy, sm - 1, sd, 0, 0, 0);
        const base = firstValidDay0(startDate, true, nonWorkingDays, workDays);
        const dayDiff = workDaysBetween(base, target, true, nonWorkingDays, workDays);
        let hourOff = 0;
        if (timePart) {
          const [h, mi] = timePart.split(':').map(Number);
          hourOff = Math.max(0, (h || 0) + (mi || 0) / 60 - dayStart);
        }
        result = dayDiff * hpd + hourOff;
      } else if (t && t.type !== 'startday') {
        const preds = deps24[id] || [];
        let mx = 0;
        if (preds.length > 0) {
          mx = Math.max(...preds.map(pid => {
            const pred = taskMap24.get(pid);
            if (pred?.type === 'startday') return parseInt(pred.text ?? '0') || 0;
            return vEnd24(pid);
          }));
        }
        if (t.earliestStart && !t.earliestStart.startsWith('W')) {
          mx = Math.max(mx, dateToHourCol(parseIsoH(t.earliestStart)));
        }
        result = mx;
      }
      vStarts24[id] = result;
      vis24.delete(id);
      return result;
    };

    // Two is24h predecessors of the SAME task can both land outside business
    // hours before the identical next work morning (e.g. one finishes Mon 19:00,
    // another Tue 06:00 — both wait for the same Tue 9am opening) and so get
    // reported as the exact same snapped column, even though only one is
    // genuinely later in real clock time. calcCriticalPath only ever sees
    // columns, so this reads as an exact tie and both branches show critical.
    // Detect these using the real (unsnapped) end times, and mark the edge
    // from the real-time loser into THIS successor as suppressed — calcCriticalPath
    // excludes it from the backward pass, so the loser gets genuine float relative
    // to this specific successor. This must NOT touch the loser's own reported
    // duration/position: a task can have several successors, and an earlier
    // version that penalized the loser's duration globally corrupted its
    // Late-Start for every OTHER successor too (a loser that also happens to
    // feed a genuinely tight, unrelated successor was wrongly excluded from the
    // critical path there as collateral damage).
    const suppressedEdges = new Set<string>();
    tasks.forEach(t => {
      if (t.type === 'callout' || t.type === 'startday') return;
      const preds = deps24[t.id] || [];
      const is24hPreds = preds
        .map(pid => taskMap24.get(pid))
        .filter((p): p is NonNullable<typeof p> =>
          !!p && p.type !== 'startday' && !!(p.alwaysOn || p.is24_5 || fullDaySchedule));
      if (is24hPreds.length < 2) return;
      const myStart = vStart24(t.id);
      const tied = is24hPreds.filter(p => Math.abs(vEnd24(p.id) - myStart) < 0.001);
      if (tied.length < 2) return;
      let winnerId = tied[0].id;
      let winnerDt = getIs24hRealEnd(winnerId);
      for (const p of tied.slice(1)) {
        const dt = getIs24hRealEnd(p.id);
        if (dt > winnerDt) { winnerDt = dt; winnerId = p.id; }
      }
      // Only suppress a candidate genuinely earlier than the winner in real time (a
      // false tie purely from both snapping to the same grid column). A candidate
      // that's ALSO exactly tied with the winner in real time is a true simultaneous
      // critical path — e.g. two 24/5 extracts that both finish at 01:30 and feed the
      // same next step — and must stay in the graph, or it silently loses its only
      // real successor and reads as having slack it doesn't actually have.
      const REAL_TIE_MS = 60000; // 1 minute — generous enough for date-hours rounding
      tied.forEach(p => {
        if (p.id === winnerId) return;
        const dt = getIs24hRealEnd(p.id);
        if (Math.abs(dt.getTime() - winnerDt.getTime()) < REAL_TIE_MS) return;
        suppressedEdges.add(`${p.id}->${t.id}`);
      });
    });

    tasks.forEach(t => {
      if (t.type === 'callout' || t.type === 'startday') return;
      const is24h = t.alwaysOn || t.is24_5;
      if (!is24h) return;
      const vs = vStart24(t.id);
      const ve = vEnd24(t.id);
      // No 0.5 floor here: computeVisualPositions' only consumer is calcCriticalPath
      // (used purely for its start/duration/float math, not for rendering — there's
      // no separate display consumer to protect from a zero-width bar). Flooring a
      // genuinely ~0-column-wide task (e.g. one that runs entirely overnight, "free"
      // under the 9-5-hours-count model) inflated its reported duration relative to
      // what its successor's real start already reflects, breaking LS≈ES continuity
      // and cutting the critical path off right at that task.
      positions.set(t.id, { start: vs, duration: Math.max(0, ve - vs) });
    });

    // Second pass: regular tasks that follow is24h tasks get an inflated raw t.start
    // (calcSchedule uses raw is24h predecessor ends, not visual-snapped ends). Fix
    // their startMap by recomputing from the visual ends of their is24h ancestors.
    const vStartRegular = (id: number, seen = new Set<number>()): number | null => {
      if (seen.has(id)) return null;
      seen.add(id);
      const t = taskMap24.get(id);
      if (!t || t.type === 'callout' || t.type === 'startday') return null;
      if (t.alwaysOn || t.is24_5) return vStart24(id);
      const preds = deps24[id] || [];
      let hasIs24hAncestor = false;
      let mx = 0;
      for (const pid of preds) {
        const pred = taskMap24.get(pid);
        if (!pred) continue;
        if (pred.type === 'startday') { mx = Math.max(mx, parseInt(pred.text ?? '0') || 0); continue; }
        if (pred.alwaysOn || pred.is24_5) {
          hasIs24hAncestor = true;
          mx = Math.max(mx, vEnd24(pid));
        } else {
          const pv = vStartRegular(pid, new Set(seen));
          if (pv !== null) { hasIs24hAncestor = true; mx = Math.max(mx, pv + pred.duration); }
          else { mx = Math.max(mx, pred.start + pred.duration); }
        }
      }
      if (!hasIs24hAncestor) return null;
      if (t.earliestStart && !t.earliestStart.startsWith('W')) {
        mx = Math.max(mx, dateToHourCol(parseIsoH(t.earliestStart)));
      }
      return mx;
    };

    tasks.forEach(t => {
      if (t.type === 'callout' || t.type === 'startday') return;
      if (t.alwaysOn || t.is24_5) return;
      const vs = vStartRegular(t.id);
      if (vs !== null) positions.set(t.id, { start: vs, duration: t.duration });
    });

    // colToDate decodes a column that lands exactly on a work-day boundary
    // (remMin === 0) as the OPENING of the following work day, not the CLOSE
    // of the current one — a deliberate, harmless choice for a start/position
    // column (both are the same instant in the pure work-hours model it
    // implements), but wrong for decoding an END specifically when comparing
    // against a real, non-collapsed clock time (see realEnds24 backfill
    // below): "Friday 17:00" and "Monday 08:00" are the same column value,
    // but only one of them is this task's actual, real-world finish moment.
    // Nudges the boundary back by a minute so colToDate's own day-walk lands
    // on the current (not the next) work day, then sets the clock to the
    // exact close-of-day time.
    const colToEndDate = (s: number): Date => {
      const totalMin = Math.round(s * 60);
      const hpdMin = hpd * 60;
      if (s > 0 && totalMin % hpdMin === 0) {
        const d = colToDate(s - 1 / 60);
        d.setHours(dayStart + hpd, 0, 0, 0);
        return d;
      }
      return colToDate(s);
    };

    // Give every REGULAR task a real-clock end too, not just is24h ones —
    // calcSchedulingConflicts otherwise falls back to a pure column comparison
    // for a regular predecessor, which can't tell apart "ends exactly at the
    // close of a work day" from "ends one unit earlier": a pinned successor
    // with a time-of-day on a NON-work day (e.g. a weekend, legitimate for an
    // alwaysOn/is24_5 task) has that day collapsed by workDaysBetween onto the
    // same work-day index as the preceding Friday, so its raw hour-of-day ends
    // up numerically smaller than the predecessor's full-day-width column —
    // a false "predecessor is late" conflict even though the pin is genuinely
    // later in real clock time. Reported live, DR2 DMLT: "Uptime data Migration
    // prep" (regular, ends Fri 2026-12-04 17:00) → "Uptime table migration
    // (incl pre-selection)" (alwaysOn, pinned Sat 2026-12-05 08:00).
    tasks.forEach(t => {
      if (t.type === 'callout' || t.type === 'startday' || realEnds24[t.id]) return;
      // `positions` only holds overrides for is24h tasks and regular tasks with
      // an is24h ancestor somewhere in their chain — a plain regular task with
      // no is24h involvement at all has no entry there and just uses its own
      // calcSchedule-computed start/duration directly, in the same column space.
      const pos = positions.get(t.id) ?? { start: t.start, duration: t.duration };
      realEnds24[t.id] = colToEndDate(pos.start + pos.duration);
    });

    return { positions, suppressedEdges, realEnds24: new Map(Object.entries(realEnds24).map(([id, d]) => [Number(id), d])) };
  }

  // Only matters in days mode with work-days-only scheduling
  if (durationUnit !== 'days' || !schedWHO) return { positions, suppressedEdges: new Set(), realEnds24: new Map() };

  // Build working-day list (same algorithm as GanttPanel's colList)
  const maxRawEnd = Math.ceil(Math.max(...tasks.map(t => t.start + t.duration + 1), 10));
  const colList: Date[] = [];
  const base = new Date(startDate); base.setHours(0, 0, 0, 0);
  for (let ci = 0; colList.length < maxRawEnd * 3 + 60; ci++) {
    const d = new Date(base); d.setDate(base.getDate() + ci);
    if (!workDays.includes(d.getDay())) continue;
    if (nwSet.has(toDs(d))) continue;
    colList.push(d);
  }

  // Index of a calendar date in the working-day list
  const dayIdx = (dateNoon: Date): number => {
    const ty = dateNoon.getFullYear(), tm = dateNoon.getMonth(), td = dateNoon.getDate();
    for (let i = 0; i < colList.length; i++) {
      const d = colList[i];
      if (d.getFullYear() === ty && d.getMonth() === tm && d.getDate() === td) return i;
      const dn = new Date(d); dn.setHours(12, 0, 0, 0);
      if (dn > dateNoon) return i;
    }
    return colList.length - 1;
  };

  const parseIso = (s: string): Date => {
    const [y, m, d] = s.split('T')[0].split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
  };

  // Calendar days from a raw duration string (for alwaysOn/24-7 tasks)
  const calDays = (durStr: string | undefined): number => {
    if (!durStr) return 1;
    const m = durStr.match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
    if (!m) return 1;
    const n = parseFloat(m[1]);
    if (n === 0) return 0;
    const u = (m[2] || 'd').toLowerCase();
    return u === 'w' ? n * 7 : u === 'h' ? Math.max(1, Math.ceil(n / 24)) : n;
  };

  // Working days for a 24/5 task duration string (skips weekends)
  const workDaysCount = (durStr: string | undefined): number => {
    if (!durStr) return 1;
    const m = durStr.match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
    if (!m) return 1;
    const n = parseFloat(m[1]);
    if (n === 0) return 0;
    const u = (m[2] || 'd').toLowerCase();
    return u === 'w' ? Math.round(n * 5) : u === 'h' ? Math.max(1, Math.ceil(n / 24)) : Math.round(n);
  };

  // Advance startD by n working days (respects workDays and nonWorkingDays)
  const addWorkDays = (startD: Date, n: number): Date => {
    const d = new Date(startD);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      if (workDays.includes(d.getDay()) && !nwSet.has(toDs(d))) added++;
    }
    return d;
  };

  // Dependency graph
  const calloutIds = new Set(tasks.filter(t => t.type === 'callout').map(t => t.id));
  const deps: Record<number, number[]> = {};
  links.forEach(a => {
    if (calloutIds.has(a.fromBox) || calloutIds.has(a.toBox)) return;
    if (!deps[a.toBox]) deps[a.toBox] = [];
    deps[a.toBox].push(a.fromBox);
  });

  const taskMap = new Map(tasks.map(t => [t.id, t]));
  const boxMap  = new Map(boxes.map(b => [b.id, b]));
  const vStarts: Record<number, number> = {};
  const vis = new Set<number>();

  const visualEnd = (id: number): number => {
    const s = visualStart(id);
    const t = taskMap.get(id);
    if (!t || t.type === 'startday') return s;
    const is24h = t.alwaysOn || t.is24_5 || fullDaySchedule;
    if (is24h && Math.floor(s) < colList.length) {
      const sd = new Date(colList[Math.floor(s)]); sd.setHours(12, 0, 0, 0);
      const boxDur = boxMap.get(id)?.duration;
      if (t.is24_5 && !t.alwaysOn && !fullDaySchedule) {
        return dayIdx(addWorkDays(sd, workDaysCount(boxDur)));
      }
      const ed = new Date(sd); ed.setDate(sd.getDate() + calDays(boxDur));
      return dayIdx(ed);
    }
    return s + t.duration;
  };

  const visualStart = (id: number): number => {
    if (vStarts[id] !== undefined) return vStarts[id];
    if (vis.has(id)) return 0; // cycle guard
    vis.add(id);
    const t = taskMap.get(id);
    let result = 0;
    if (t?.scheduledStart) {
      result = dayIdx(parseIso(t.scheduledStart));
    } else if (t && t.type !== 'startday') {
      const preds = deps[id] || [];
      let mx = 0;
      if (preds.length > 0) {
        mx = Math.max(...preds.map(pid => {
          const pred = taskMap.get(pid);
          if (pred?.type === 'startday') return parseInt(pred.text ?? '0') || 0;
          return visualEnd(pid);
        }));
      }
      if (t.earliestStart && !t.earliestStart.startsWith('W')) {
        mx = Math.max(mx, dayIdx(parseIso(t.earliestStart)));
      }
      result = mx;
    }
    vStarts[id] = result;
    vis.delete(id);
    return result;
  };

  tasks.forEach(t => {
    if (t.type === 'callout' || t.type === 'startday') return;
    const vs = visualStart(t.id);
    const is24h = t.alwaysOn || t.is24_5 || fullDaySchedule;
    let dur = t.duration;
    if (is24h && Math.floor(vs) < colList.length) {
      const sd = new Date(colList[Math.floor(vs)]); sd.setHours(12, 0, 0, 0);
      const boxDur = boxMap.get(t.id)?.duration;
      const ed = (t.is24_5 && !t.alwaysOn && !fullDaySchedule)
        ? addWorkDays(sd, workDaysCount(boxDur))
        : (() => { const d = new Date(sd); d.setDate(d.getDate() + calDays(boxDur)); return d; })();
      dur = Math.max(0.5, dayIdx(ed) - vs);
    }
    positions.set(t.id, { start: vs, duration: dur });
  });

  return { positions, suppressedEdges: new Set(), realEnds24: new Map() };
}

/**
 * Returns box and arrow IDs involved in scheduling conflicts:
 * a task has a pinned scheduledStart that falls before a predecessor finishes.
 */
export function calcSchedulingConflicts(
  tasks: ScheduledTask[],
  links: ScheduleLink[],
  visualPositions?: VisualPositions,
): { boxIds: Set<number>; arrowIds: Set<number> } {
  const boxIds = new Set<number>();
  const arrowIds = new Set<number>();
  const calloutIds = new Set(tasks.filter(t => t.type === 'callout').map(t => t.id));
  const taskMap = new Map(tasks.map(t => [t.id, t]));

  tasks
    .filter(t => t.scheduledStart)
    .forEach(t => {
      // A successor pinned to an explicit time-of-day can start at any real clock
      // time, not just 9am. The default check (endColForConflict > t.start) compares
      // "next work morning" grid columns, which can't distinguish a predecessor
      // finishing at 2am from one finishing at 9am -- both snap to the same column,
      // silently masking a real conflict against an early time-of-day pin. When we
      // have the predecessor's real (unsnapped) end available, compare real clock
      // times directly instead.
      const [datePart, timePart] = t.scheduledStart!.split('T');
      let pinnedDt: Date | null = null;
      if (timePart) {
        const [sy, sm, sd] = datePart.split('-').map(Number);
        const [h, mi] = timePart.split(':').map(Number);
        pinnedDt = new Date(sy, sm - 1, sd, h || 0, mi || 0, 0);
      }

      links
        .filter(a => a.toBox === t.id && !calloutIds.has(a.fromBox))
        .forEach(a => {
          const p = taskMap.get(a.fromBox);
          if (!p) return;
          const realEnd = pinnedDt ? visualPositions?.realEnds24.get(p.id) : undefined;
          const conflict = realEnd ? realEnd > pinnedDt! : p.endColForConflict > t.start;
          if (conflict) {
            boxIds.add(t.id);
            if (a.id != null) arrowIds.add(a.id);
          }
        });
    });

  return { boxIds, arrowIds };
}
